import React, { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useUserAuth } from '../context/UserAuthContext';
import Logo from '../assets/headerlogo.png';
import SearchBar from './SearchBar';

const Header = ({ onLogout, idToken, userData: userDataProp, menuExpanded, setMenuExpanded, isMobile, searchQuery, setSearchQuery }) => {
    const [anchorElUser, setAnchorElUser] = useState(null);
    const { user } = useUserAuth();
    const userData = userDataProp || user;

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleCopyIdToken = async () => {
        try {
            await navigator.clipboard.writeText(`Bearer ${idToken}`);
            alert('ID Token copied to clipboard');
        } catch (err) {
            console.error('Failed to copy ID token:', err);
        }
    };

    // Extract first letter of user name
    const avatarLetter = userData?.name?.[0]?.toUpperCase() || userData?.displayName?.[0]?.toUpperCase() || '';

    if (!userData) {
        return <div style={{ padding: 16, color: '#fff' }}>Loading...</div>;
    }

    // Mobile Header - Top Navigation Bar
    if (isMobile) {
        return (
            <Box
                sx={{
                    backgroundColor: '#181F2A',
                    height: '64px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                }}
            >
                {/* Logo on the left */}
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 0, minWidth: '60px' }}>
                    <img 
                        src={Logo} 
                        alt="Isabel Logo" 
                        style={{ 
                            height: '28px', 
                            width: 'auto'
                        }} 
                    />
                </Box>

                {/* Search Bar in center */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: 1 }}>
                    <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} inHeader={true} />
                </Box>

                {/* Avatar on the right */}
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 0, minWidth: '60px', justifyContent: 'flex-end' }}>
                    <Tooltip title="Open settings">
                        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                            <Avatar 
                                alt="User Avatar" 
                                sx={{ 
                                    width: 32, 
                                    height: 32, 
                                    bgcolor: '#F9AA33',
                                    color: '#fff',
                                    fontSize: '14px',
                                    fontWeight: 600
                                }}
                            >
                                {avatarLetter}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* User Menu */}
                <Menu
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    keepMounted
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    sx={{
                        '& .MuiPaper-root': {
                            backgroundColor: '#222C2F',
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 2,
                        }
                    }}
                >
                    <MenuItem sx={{ color: '#fff', fontSize: 14 }}>
                        {userData.name || userData.displayName || 'User'}
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            handleCopyIdToken();
                            handleCloseUserMenu();
                        }}
                        sx={{ color: '#fff', fontSize: 14 }}
                    >
                        Copy ID Token
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            onLogout();
                            handleCloseUserMenu();
                        }}
                        sx={{ color: '#fff', fontSize: 14 }}
                    >
                        Logout
                    </MenuItem>
                </Menu>
            </Box>
        );
    }

    // Desktop Header - Top Navigation Bar (same as mobile)
    return (
        <Box
            sx={{
                backgroundColor: '#181F2A',
                height: '64px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)',
                width: '100%',
                zIndex: 1000,
            }}
        >
            {/* Logo on the left */}
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 0, minWidth: '60px' }}>
                <img 
                    src={Logo} 
                    alt="Isabel Logo" 
                    style={{ 
                        height: '28px', 
                        width: 'auto'
                    }} 
                />
            </Box>

            {/* Search Bar in center */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: 2 }}>
                <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} inHeader={true} />
            </Box>

            {/* Avatar on the right */}
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 0, minWidth: '60px', justifyContent: 'flex-end' }}>
                <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                        <Avatar 
                            alt="User Avatar" 
                            sx={{ 
                                width: 32, 
                                height: 32, 
                                bgcolor: '#F9AA33',
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 600
                            }}
                        >
                            {avatarLetter}
                        </Avatar>
                    </IconButton>
                </Tooltip>
            </Box>

            {/* User Menu */}
            <Menu
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: '#222C2F',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 2,
                    }
                }}
            >
                <MenuItem sx={{ color: '#fff', fontSize: 14 }}>
                    {userData.name || userData.displayName || 'User'}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleCopyIdToken();
                        handleCloseUserMenu();
                    }}
                    sx={{ color: '#fff', fontSize: 14 }}
                >
                    Copy ID Token
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        onLogout();
                        handleCloseUserMenu();
                    }}
                    sx={{ color: '#fff', fontSize: 14 }}
                >
                    Logout
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default Header;
